import { useState } from 'react';
import { CheckCircle, Eye, Layers, RefreshCw, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { BulkUpdateJobItemsSidebar } from './BulkUpdateJobItemsSidebar';
import { useBulkJobRealtime } from './hooks/useBulkJobRealtime';
import { useBulkUpdateJobs } from './hooks/useBulkUpdateJobs';
import type { BulkUpdateJobFilters, BulkUpdateJobSummary } from '@/types/agent/bulk-update';

const PAGE_SIZE = 50;

const BulkUpdateJobsPage = () => {
  const [filters, setFilters] = useState<BulkUpdateJobFilters>({});
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<BulkUpdateJobSummary | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { data: jobsData, isLoading, isFetching, refetch } = useBulkUpdateJobs(filters, page, PAGE_SIZE);
  useBulkJobRealtime();

  const liveSelectedJob = jobsData?.data.find((job) => job.id === selectedJob?.id) ?? selectedJob;

  const handleViewItems = (job: BulkUpdateJobSummary) => {
    setSelectedJob(job);
    setIsDetailOpen(true);
  };

  const handleRefresh = async () => {
    const { isError } = await refetch();
    if (isError) toast.error('Could not refresh the jobs');
  };

  const getStatusBadge = (status: string | null) => {
    const statusMap: Record<string, { icon: any; className: string; label: string }> = {
      pending: { icon: RefreshCw, className: 'bg-genie-background-muted text-genie-text-muted border-genie-border', label: 'Pending' },
      processing: { icon: RefreshCw, className: 'bg-genie-primary-light text-genie-primary border-genie-primary/20', label: 'Processing' },
      completed: { icon: CheckCircle, className: 'bg-genie-primary-light text-genie-primary border-genie-primary/20', label: 'Completed' },
      failed: { icon: XCircle, className: 'bg-genie-error-light text-genie-error border-genie-error/20', label: 'Failed' },
    };

    const displayStatus = status || 'pending';
    const config = statusMap[displayStatus] || statusMap.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, 'inline-flex items-center gap-1 border w-fit')}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Layers className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Bulk Update Jobs</h1>
              <p className="text-genie-text-secondary">Track bulk agent update jobs and their item-level results</p>
            </div>
          </div>
          <Button variant="outline" className="min-h-[44px]" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-2', isFetching && 'animate-spin')} />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? undefined : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setFilters({})} className="w-full">
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Items</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading bulk update jobs...
                    </TableCell>
                  </TableRow>
                ) : jobsData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-genie-text-muted">
                      No bulk update jobs found
                    </TableCell>
                  </TableRow>
                ) : (
                  jobsData?.data.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-medium text-genie-text-primary">#{job.id}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary max-w-xs">{job.label}</TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{job.total}</TableCell>
                      <TableCell className="text-sm text-genie-primary">{job.completed}</TableCell>
                      <TableCell className="text-sm text-genie-error">{job.failed}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{job.pending}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{new Date(job.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleViewItems(job)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {isLoading ? (
              <div className="text-center py-8 text-genie-text-secondary">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                <p>Loading bulk update jobs...</p>
              </div>
            ) : jobsData?.data.length === 0 ? (
              <div className="text-center py-8 text-genie-text-muted">No bulk update jobs found</div>
            ) : (
              <div className="space-y-3 p-4">
                {jobsData?.data.map((job) => (
                  <div key={job.id} className="border border-genie-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-genie-text-primary text-sm">Job #{job.id}</p>
                      <Button variant="ghost" size="sm" onClick={() => handleViewItems(job)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>

                    {getStatusBadge(job.status)}

                    <div className="text-xs text-genie-text-secondary space-y-1">
                      <p><span className="text-genie-text-muted">Updated:</span> {job.label}</p>
                      <p><span className="text-genie-text-muted">Total:</span> {job.total}</p>
                      <p><span className="text-genie-text-muted">Completed:</span> {job.completed}</p>
                      <p><span className="text-genie-text-muted">Failed:</span> {job.failed}</p>
                      <p><span className="text-genie-text-muted">Pending:</span> {job.pending}</p>
                      <p><span className="text-genie-text-muted">Created:</span> {new Date(job.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {jobsData && jobsData.count > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, jobsData.count)} of {jobsData.count} jobs
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * PAGE_SIZE >= jobsData.count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Item Detail Sidebar */}
        <BulkUpdateJobItemsSidebar key={selectedJob?.id} isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} job={liveSelectedJob} />
      </div>
    </div>
  );
};

export default BulkUpdateJobsPage;
